import React, { useState, useEffect } from 'react';
import { api } from '../api';

const CommunityActivityFeed = ({ user }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [loadingNewsletter, setLoadingNewsletter] = useState(false);

  useEffect(() => {
    if (user?.hoa_id) {
      loadCommunityActivity();
    }
  }, [user?.hoa_id]);

  const loadCommunityActivity = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/community-activity/${user.hoa_id}?limit=10`);
      setActivities(data.activities || []);
    } catch (error) {
      console.error('Error loading community activity:', error);
      // Fallback to sample activity if API fails
      setActivities([
        {
          id: 'sample-1',
          type: 'newsletter',
          title: 'Newsletter Published: Community Newsletter',
          description: 'Catch up on the latest happenings in your community',
          created_at: new Date().toISOString(),
          created_by_name: 'HOA Board',
          action_button: {
            text: 'Read Newsletter',
            action: 'view_newsletter'
          }
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadNewsletter = async (newsletterId) => {
    setLoadingNewsletter(true);
    try {
      const { data } = await api.get(`/api/newsletter/${newsletterId}`);
      setSelectedNewsletter(data);
      setShowNewsletterModal(true);
    } catch (error) {
      console.error('Error loading newsletter:', error);
      alert('Unable to load newsletter. You may not have permission to view this content.');
    } finally {
      setLoadingNewsletter(false);
    }
  };

  const handleActionClick = (activity) => {
    if (activity.action_button?.action === 'view_newsletter') {
      // Load and display newsletter in modal
      if (activity.reference_id) {
        loadNewsletter(activity.reference_id);
      } else {
        alert('Newsletter not available for viewing.');
      }
    } else if (activity.action_button?.action === 'view_announcement') {
      // Navigate to announcements tab
      console.log('Navigate to announcement:', activity.reference_id);
    } else if (activity.action_button?.action === 'view_violation') {
      // Navigate to violations tab
      console.log('Navigate to violation:', activity.reference_id);
    } else if (activity.action_button?.action === 'add_agenda') {
      // Navigate to events tab and open agenda modal for the specific event
      const eventId = activity.action_button.reference_id;
      if (eventId) {
        // This would need to be implemented with proper routing context
        // For now, redirect to events tab with event ID
        window.location.hash = `events?event=${eventId}&action=add_agenda`;
      }
    }
  };

  const getActivityIcon = (type, announcementType) => {
    switch (type) {
      case 'newsletter':
        return {
          icon: (
            <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
          ),
          bgColor: 'bg-green-500'
        };
      case 'announcement':
        switch (announcementType) {
          case 'emergency':
            return {
              icon: '🚨',
              bgColor: 'bg-red-500'
            };
          case 'project':
            return {
              icon: '🏗️',
              bgColor: 'bg-blue-500'
            };
          case 'voting':
            return {
              icon: '🗳️',
              bgColor: 'bg-purple-500'
            };
          default:
            return {
              icon: '📢',
              bgColor: 'bg-yellow-500'
            };
        }
      case 'violation':
        return {
          icon: '⚠️',
          bgColor: 'bg-orange-500'
        };
      case 'agenda_reminder':
        return {
          icon: '📋',
          bgColor: 'bg-purple-500'
        };
      default:
        return {
          icon: (
            <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ),
          bgColor: 'bg-gray-500'
        };
    }
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const activityDate = new Date(dateString);
    const diffMs = now - activityDate;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return activityDate.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Community Activity</h3>
          <div className="animate-pulse">
            <div className="space-y-4">
              <div className="flex space-x-3">
                <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Community Activity</h3>
        
        {activities.length === 0 ? (
          <div className="text-center py-6">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No recent activity</h3>
            <p className="mt-1 text-sm text-gray-500">
              Community updates and announcements will appear here
            </p>
          </div>
        ) : (
          <div className="flow-root">
            <ul className="-mb-8">
              {activities.map((activity, index) => {
                const iconData = getActivityIcon(activity.type, activity.announcement_type);
                const isLast = index === activities.length - 1;
                
                return (
                  <li key={activity.id}>
                    <div className={`relative ${!isLast ? 'pb-8' : ''}`}>
                      {!isLast && (
                        <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                      )}
                      
                      <div className="relative flex space-x-3">
                        <div>
                          <span className={`h-8 w-8 rounded-full ${iconData.bgColor} flex items-center justify-center ring-8 ring-white`}>
                            {typeof iconData.icon === 'string' ? (
                              <span className="text-sm">{iconData.icon}</span>
                            ) : (
                              iconData.icon
                            )}
                          </span>
                        </div>
                        
                        <div className="min-w-0 flex-1 pt-1.5">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {activity.title}
                              </p>
                              {activity.description && (
                                <p className="text-sm text-gray-500 mt-1">
                                  {activity.description}
                                </p>
                              )}
                              
                              {/* Project cost display */}
                              {activity.project_cost && (
                                <div className="mt-2">
                                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-green-100 text-green-800">
                                    💰 ${activity.project_cost.toLocaleString()}
                                  </span>
                                </div>
                              )}
                              
                              {/* Attachments indicator */}
                              {activity.has_attachments && (
                                <div className="mt-2">
                                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800">
                                    📎 Has attachments
                                  </span>
                                </div>
                              )}
                              
                              {/* Action button */}
                              {activity.action_button && (
                                <div className="mt-3">
                                  <button
                                    onClick={() => handleActionClick(activity)}
                                    className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    {activity.action_button.text}
                                    <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                              
                              <div className="mt-2 flex items-center text-xs text-gray-500">
                                <span>{activity.created_by_name}</span>
                                <span className="mx-2">•</span>
                                <time>{getTimeAgo(activity.created_at)}</time>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Newsletter Modal */}
      {showNewsletterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {loadingNewsletter ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-4">Loading newsletter...</p>
              </div>
            ) : selectedNewsletter ? (
              <div className="p-6">
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedNewsletter.title}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedNewsletter.issue_date && `Issue Date: ${new Date(selectedNewsletter.issue_date).toLocaleDateString()}`}
                      {selectedNewsletter.published_at && ` • Published: ${new Date(selectedNewsletter.published_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowNewsletterModal(false);
                      setSelectedNewsletter(null);
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Newsletter Content */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="prose prose-lg max-w-none">
                    {selectedNewsletter.sections && selectedNewsletter.sections.length > 0 ? (
                      selectedNewsletter.sections
                        .filter(section => section.enabled && section.html && section.html.trim())
                        .map((section, index) => (
                          <div key={section.key || index} className="mb-6 pb-4 border-b border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h3>
                            <div 
                              dangerouslySetInnerHTML={{ __html: section.html }}
                            />
                            {/* Simple reactions for modal */}
                            <div className="mt-3 pt-2 border-t border-gray-50">
                              <div className="flex items-center space-x-3">
                                <span className="text-xs text-gray-500">React:</span>
                                <div className="flex space-x-1">
                                  {['❤️', '😊', '❗'].map((emoji, i) => (
                                    <button
                                      key={i}
                                      className="p-1 hover:bg-gray-100 rounded text-sm transition-colors"
                                      title={`React with ${emoji}`}
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                    ) : (
                      <p className="text-gray-500">No content available</p>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-6 pt-4 border-t border-gray-200 text-center text-sm text-gray-500">
                  <p>This newsletter was published by your HOA board.</p>
                  {selectedNewsletter.author_name && (
                    <p>Created by: {selectedNewsletter.author_name}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center">
                <h3 className="text-lg font-medium text-gray-900">Newsletter Not Available</h3>
                <p className="text-gray-500 mt-2">The requested newsletter could not be loaded.</p>
                <button
                  onClick={() => {
                    setShowNewsletterModal(false);
                    setSelectedNewsletter(null);
                  }}
                  className="mt-4 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityActivityFeed;